/**
 * Drifting dot field with links between near neighbours, and a hover that pushes
 * dots away.
 *
 * Replaces particles.js 2.0, abandoned since 2016 and configured in the old code
 * through an eighty-line JSON blob. The visual parameters below are carried over
 * from that configuration so the hero looks the same.
 */

interface Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

const CONFIG = {
  count: 47,
  colour: '#ffffff',
  dotOpacity: 0.22,
  lineOpacity: 0.21,
  linkDistance: 158,
  speed: 0.6,
  maxRadius: 3,
  repulseRadius: 120,
  repulseStrength: 2.2,
} as const;

/**
 * Whether the reduced-motion preference freezes the field.
 *
 * Off deliberately. The effect this replaced animated unconditionally, and the
 * drift is the point of it — a frozen field is a different design, not a gentler
 * one. Note the consequence honestly: a visitor who has asked for reduced motion
 * still gets a slowly moving background here.
 *
 * Setting this true restores the accommodation: a single static frame is drawn,
 * and neither the animation loop nor the pointer repulsion starts.
 */
const RESPECT_REDUCED_MOTION = false;

export interface ParticleField {
  destroy(): void;
}

export function initParticles(container: HTMLElement): ParticleField | null {
  // When the accommodation is enabled, reduced motion suppresses the animation
  // but not the artwork: a single static frame is still drawn, because an empty
  // 350px band reads as a broken section rather than a deliberate one.
  const animate =
    !RESPECT_REDUCED_MOTION || !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block';
  container.appendChild(canvas);

  const context = canvas.getContext('2d');

  if (!context) {
    canvas.remove();
    return null;
  }

  let dots: Dot[] = [];
  let frame = 0;
  let pointer: { x: number; y: number } | null = null;
  let ratio = 1;

  function resize(): void {
    ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = container.clientWidth * ratio;
    canvas.height = container.clientHeight * ratio;
    context!.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  function seed(): void {
    dots = Array.from({ length: CONFIG.count }, () => ({
      x: Math.random() * container.clientWidth,
      y: Math.random() * container.clientHeight,
      vx: (Math.random() - 0.5) * CONFIG.speed,
      vy: (Math.random() - 0.5) * CONFIG.speed,
      radius: 1 + Math.random() * (CONFIG.maxRadius - 1),
    }));
  }

  function step(): void {
    const width = container.clientWidth;
    const height = container.clientHeight;

    context!.clearRect(0, 0, width, height);

    for (const dot of dots) {
      dot.x += dot.vx;
      dot.y += dot.vy;

      // Wrap rather than bounce, matching the original "out" mode.
      if (dot.x < 0) dot.x = width;
      if (dot.x > width) dot.x = 0;
      if (dot.y < 0) dot.y = height;
      if (dot.y > height) dot.y = 0;

      if (pointer) {
        const dx = dot.x - pointer.x;
        const dy = dot.y - pointer.y;
        const distance = Math.hypot(dx, dy);

        if (distance > 0 && distance < CONFIG.repulseRadius) {
          const push = (1 - distance / CONFIG.repulseRadius) * CONFIG.repulseStrength;
          dot.x += (dx / distance) * push;
          dot.y += (dy / distance) * push;
        }
      }
    }

    context!.strokeStyle = CONFIG.colour;
    context!.lineWidth = 1;

    for (let i = 0; i < dots.length; i++) {
      for (let j = i + 1; j < dots.length; j++) {
        const distance = Math.hypot(dots[i].x - dots[j].x, dots[i].y - dots[j].y);

        if (distance > CONFIG.linkDistance) {
          continue;
        }

        context!.globalAlpha = CONFIG.lineOpacity * (1 - distance / CONFIG.linkDistance);
        context!.beginPath();
        context!.moveTo(dots[i].x, dots[i].y);
        context!.lineTo(dots[j].x, dots[j].y);
        context!.stroke();
      }
    }

    context!.fillStyle = CONFIG.colour;
    context!.globalAlpha = CONFIG.dotOpacity;

    for (const dot of dots) {
      context!.beginPath();
      context!.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
      context!.fill();
    }

    if (animate) {
      frame = requestAnimationFrame(step);
    }
  }

  function onPointerMove(event: PointerEvent): void {
    const bounds = container.getBoundingClientRect();
    pointer = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  function onPointerLeave(): void {
    pointer = null;
  }

  /**
   * Resizing has to repaint, not just re-measure.
   *
   * Assigning canvas.width clears the canvas. While the animation loop is
   * running the next frame redraws immediately, so that is invisible — but with
   * reduced motion there is no next frame, so a single resize would blank the
   * artwork permanently. Browsers fire a resize during startup, which is exactly
   * how this showed up: a canvas correctly sized and a band still empty.
   */
  function onResize(): void {
    resize();

    if (!animate) {
      step();
    }
  }

  resize();
  seed();
  step();

  window.addEventListener('resize', onResize);

  if (animate) {
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerleave', onPointerLeave);
  }

  return {
    destroy(): void {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerleave', onPointerLeave);
      canvas.remove();
    },
  };
}
